"use client";

import { type FilterOption } from "@/types/filters";
import React, { useCallback, useEffect, useState } from "react";
import FilterSection from "./FilterSection";

interface VariantFilters {
  sizes: string[];
  colors: string[];
  conditions: string[];
}

interface VariantsFilterPanelProps {
  onApplyFilters: (filters: VariantFilters) => void;
  currentFilters: VariantFilters;
  availableSizes: FilterOption[];
  availableColors: FilterOption[];
  availableConditions: FilterOption[];
  onClose: () => void;
  totalVariantCount: number;
}

// Utility function to handle filter option toggling
const toggleFilterOption = (currentArray: string[], value: string, checked: boolean): string[] => {
  if (checked) {
    if (value === "all") {
      // If "All" is selected, clear all other selections
      return ["all"];
    } else {
      // Remove "all" if it's selected and add the specific option
      const newArray = currentArray.filter((item) => item !== "all");
      return [...newArray, value];
    }
  } else {
    if (value === "all") {
      // If "All" is deselected, keep all other selections
      return currentArray.filter((item) => item !== "all");
    } else {
      // Remove the specific option
      const newArray = currentArray.filter((item) => item !== value);
      
      // If no specific options are selected, default to "all"
      if (newArray.length === 0) {
        return ["all"];
      }
      return newArray;
    }
  }
};

const VariantsFilterPanel: React.FC<VariantsFilterPanelProps> = ({
  onApplyFilters,
  currentFilters,
  availableSizes,
  availableColors,
  availableConditions,
  onClose,
  totalVariantCount,
}) => {

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const [filters, setFilters] = useState<VariantFilters>(currentFilters);
  const [openSections, setOpenSections] = useState({
    sizes: (Array.isArray(availableSizes) ? availableSizes.length : 0) > 2, // "All" + the variant's size
    colors: (Array.isArray(availableColors) ? availableColors.length : 0) > 2, // "All" + the variant's color
    conditions: (Array.isArray(availableConditions) ? availableConditions.filter(condition => condition.listingCount > 0).length : 0) > 2, // "All" + the variant's condition
  });

  const handleArrayFilterChange = useCallback((
    key: keyof VariantFilters,
    value: string | number,
    checked: boolean
  ) => {
    const currentArray = filters[key] as string[];
    const stringValue = String(value);
    
    try {
      const newArray = toggleFilterOption(currentArray, stringValue, checked);
      const newFilters = { ...filters, [key]: newArray };
      setFilters(newFilters);
    } catch (error) {
      console.error("Error updating filter:", error);
      // Fallback to current state if there's an error
    }
  }, [filters]);

  const handleApplyFilters = useCallback(() => {
    // Validate filters before applying
    const validatedFilters = {
      sizes: Array.isArray(filters.sizes) ? filters.sizes : ["all"],
      colors: Array.isArray(filters.colors) ? filters.colors : ["all"],
      conditions: Array.isArray(filters.conditions) ? filters.conditions : ["all"],
    };
    
    onApplyFilters(validatedFilters);
    onClose();
  }, [filters, onApplyFilters, onClose]);

  const toggleSection = useCallback((section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Validate filter options arrays
  const validatedSizes = Array.isArray(availableSizes) ? availableSizes : [];
  const validatedColors = Array.isArray(availableColors) ? availableColors : [];
  const validatedConditions = Array.isArray(availableConditions) ? availableConditions : [];

  // Helper functions for FilterSection components
  const getStandardSummaryText = (options: FilterOption[]) => {
    return options.length === 2 ? 
      options.find(option => option.rawValue !== "all")?.displayLabel || "All" :
      `All (${options.length - 1})`;
  };

  const getConditionSummaryText = (options: FilterOption[]) => {
    const nonEmptyCount = options.filter(condition => condition.rawValue !== "all" && condition.listingCount > 0).length;
    return nonEmptyCount === 1 ? 
      options.find(condition => condition.rawValue !== "all" && condition.listingCount > 0)?.displayLabel || "All" :
      `All (${nonEmptyCount})`;
  };

  const shouldDisableStandardSection = (options: FilterOption[]) => options.length === 2;
  const shouldDisableConditionSection = (options: FilterOption[]) => {
    const nonEmptyCount = options.filter(condition => condition.rawValue !== "all" && condition.listingCount > 0).length;
    return nonEmptyCount <= 1;
  };
  const shouldDisableConditionOption = (option: FilterOption) => option.listingCount === 0;

  return (
    <div className="fixed inset-0 z-[9000] flex flex-col bg-white h-screen overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 flex-shrink-0">
        <div className="w-6"></div>
        <h2 className="text-md font-raleway font-bold text-black">
          Variants ({totalVariantCount})
        </h2>
        <button onClick={onClose} className="text-md">
          ✕
        </button>
      </div>

      {/* Divider */}
      <div className="mx-4 border-b border-light-grey flex-shrink-0"></div>

      {/* Filter Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
        {/* Size Filter */}
        <FilterSection
          title="SIZE"
          options={validatedSizes}
          selectedValues={filters.sizes}
          onToggle={(value: string, checked: boolean) => handleArrayFilterChange("sizes", value, checked)}
          isOpen={openSections.sizes}
          onToggleSection={() => toggleSection("sizes")}
          getSummaryText={getStandardSummaryText}
          shouldDisableSection={shouldDisableStandardSection}
        />

        {/* Color Filter */}
        <FilterSection
          title="COLOR"
          options={validatedColors}
          selectedValues={filters.colors}
          onToggle={(value: string, checked: boolean) => handleArrayFilterChange("colors", value, checked)}
          isOpen={openSections.colors}
          onToggleSection={() => toggleSection("colors")}
          getSummaryText={getStandardSummaryText}
          shouldDisableSection={shouldDisableStandardSection}
        />

        {/* Condition Filter */}
        <FilterSection
          title="CONDITION"
          options={validatedConditions}
          selectedValues={filters.conditions}
          onToggle={(value: string, checked: boolean) => handleArrayFilterChange("conditions", value, checked)}
          isOpen={openSections.conditions}
          onToggleSection={() => toggleSection("conditions")}
          getSummaryText={getConditionSummaryText}
          shouldDisableSection={shouldDisableConditionSection}
          shouldDisableOption={shouldDisableConditionOption}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <button
          onClick={handleApplyFilters}
          className="w-full py-3 bg-white text-black border border-black font-raleway font-semibold"
        >
          APPLY FILTERS
        </button>
      </div>
    </div>
  );
};

export default VariantsFilterPanel; 